import { notifyUpdateAvailable, setApplyUpdate } from './updateStore.js';

/**
 * `?dev` query param — a kill-switch that unregisters any installed SW and
 * bypasses its caches, so a production issue is debuggable without one
 * (typescript.md: "honor a `?dev` query-param kill-switch that unregisters
 * the SW + bypasses its caches").
 */
const DEV_KILL_SWITCH_PARAM = 'dev';

function hasDevKillSwitch(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has(DEV_KILL_SWITCH_PARAM);
}

/** Unregisters every SW registration for this origin and clears every Cache Storage entry. */
async function unregisterAndBypassCaches(): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
  if (typeof caches !== 'undefined') {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
}

/**
 * PROD-ONLY service worker registration (typescript.md: "register the SW
 * solely in a production build ... never in dev — stale-cache hell, breaks
 * HMR/fresh assets"), with the `?dev` kill-switch above taking priority even
 * in a prod build. `registerType: 'prompt'` (vite.config.ts) means the SW
 * never force-activates a new version on its own — `onNeedRefresh` here
 * feeds the SAME update store `DeployUpdateToast.tsx` listens to for the
 * `deploy:broadcast` push (E9 §10.3: "Belt-and-suspenders: `onNeedRefresh`
 * drives the SAME toast component").
 *
 * Call once, near app bootstrap (see `./WIRING.md` for the exact call site
 * — `main.tsx` is outside this task's ownership).
 */
export async function registerServiceWorker(): Promise<void> {
  if (hasDevKillSwitch()) {
    await unregisterAndBypassCaches();
    return;
  }
  if (!import.meta.env.PROD) return;
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

  const { registerSW } = await import('virtual:pwa-register');
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      notifyUpdateAvailable('sw');
    },
    onRegisterError(error: unknown) {
      // No UI surface of its own — a failed SW registration degrades the app
      // to "no offline support this session", not a broken app; log for
      // debugging rather than surfacing an error toast for a background,
      // non-blocking concern (no dedicated error-tracking sink wired into
      // apps/web yet — open issue, see WIRING.md).
      console.error('registerServiceWorker: service worker registration failed', error);
    },
  });
  setApplyUpdate(() => updateSW(true));
}
